import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { BaseApiService } from '@app/core/services/api/base-api/base-api.service';
import { API_CONSTANTS } from '@app/core/constants/api.constants';
import { IOnboardingStatus, IOnboardingSteps, OnboardingTourStep } from '../interfaces/onboarding.interface';

export const ONBOARDING_STORAGE = {
    /** This visit (browser tab): the coach closed the checklist with X. It comes back next visit. */
    CLOSED: 'saideline.onboarding.closed',
} as const;

/**
 * Getting-started checklist of the signed-in coach. Keeps the last known status so the
 * checklist and the pages completing its steps share one source of truth.
 * Failures stay silent (see `API_CONSTANTS.SILENT_URLS`): the checklist is a nice-to-have.
 */
@Injectable({
    providedIn: 'root',
})
export class OnboardingService extends BaseApiService {
    private readonly statusSubject$ = new BehaviorSubject<IOnboardingStatus | null>(null);
    readonly status$: Observable<IOnboardingStatus | null> = this.statusSubject$.asObservable();

    private readonly closedSubject$ = new BehaviorSubject<boolean>(
        this.read(sessionStorage, ONBOARDING_STORAGE.CLOSED) === 'true'
    );
    /** The coach closed the checklist for this visit. */
    readonly closed$: Observable<boolean> = this.closedSubject$.asObservable();

    private historyMarked = false;

    constructor(http: HttpClient) {
        super(http, API_CONSTANTS.ENDPOINTS.ONBOARDING);
    }

    /** Re-reads the status, e.g. after the coach added a player or a match. */
    refresh(): void {
        this.get<IOnboardingStatus>().subscribe({ next: (status) => this.statusSubject$.next(status), error: () => {} });
    }

    /** Re-reads the status after the coach did something that may tick `step`, while that step is still open. */
    refreshIfOpen(step: keyof IOnboardingSteps): void {
        const current = this.statusSubject$.value;
        if (!current || current.dismissed || current.steps[step]) {
            return;
        }
        this.refresh();
    }

    /** Notes the first visit to the training history on the account (for the insights); once per visit. */
    markHistoryViewed(): void {
        if (this.historyMarked) {
            return;
        }
        this.historyMarked = true;
        this.post<IOnboardingStatus>('/history-viewed', {}).subscribe({
            next: (status) => this.statusSubject$.next(status),
            error: () => {},
        });
    }

    /** Thumbs up or down for one of the coach's sessions; the backend keeps the first answer per session. */
    rateSession(liked: boolean, sessionId: string): Observable<{ rated: boolean }> {
        return this.post<{ rated: boolean }>('/session-feedback', { liked, sessionId });
    }

    /** Gives the sidekick, the coach's personal co-coach, a name (or a new one). */
    nameSidekick(name: string): Observable<{ sidekickName: string }> {
        return this.post<{ sidekickName: string }>('/sidekick-name', { name });
    }

    /** Saves where the co-coach's onboarding stands; the backend keeps `done` once reached. */
    saveTourStep(step: OnboardingTourStep): Observable<{ onboardingTourStep: OnboardingTourStep }> {
        return this.post<{ onboardingTourStep: OnboardingTourStep }>('/tour-step', { step });
    }

    /** Notes a message of the co-coach on the account, so no other device shows it again. */
    markSidekickSeen(event: string): void {
        this.post<{ sidekickSeen: string[] }>('/sidekick-seen', { event }).subscribe({ error: () => {} });
    }

    /** Closes the checklist until the next visit; the steps stay open. */
    closeForNow(): void {
        this.closedSubject$.next(true);
        this.write(sessionStorage, ONBOARDING_STORAGE.CLOSED, 'true');
    }

    /** Hides the checklist right away and for good (offered once every step is done). */
    dismiss(): void {
        const current = this.statusSubject$.value;
        if (current) {
            this.statusSubject$.next({ ...current, dismissed: true });
        }
        this.post<IOnboardingStatus>('/dismiss', {}).subscribe({ error: () => {} });
    }

    /** Forgets the status when the coach signs out. */
    clear(): void {
        this.statusSubject$.next(null);
    }

    private read(storage: Storage, key: string): string | null {
        try {
            return storage.getItem(key);
        } catch {
            return null;
        }
    }

    private write(storage: Storage, key: string, value: string): void {
        try {
            storage.setItem(key, value);
        } catch {
            // Storage blocked: the in-memory state applies until the next reload
        }
    }
}
